import type { IconName as BaseIconName } from "@repo/icons/base";
import baseSprite from "@repo/icons/base/sprite";
import type { IconName as ConnectionIconName } from "@repo/icons/connections";
import connectionSprite from "@repo/icons/connections/sprite";
import type { IconName as CustomIconName } from "@repo/icons/custom";
import customSprite from "@repo/icons/custom/sprite";
import type { IconName as LogoIconName } from "@repo/icons/logos";
import logoSprite from "@repo/icons/logos/sprite";
import type { DatabaseType, DataType } from "@repo/lanes/types";
import type { ColumnFormat } from "@repo/shared/types/column-format";
import type { DisplayType, GraphType } from "@repo/shared/types/graph-config";
import type { SortType } from "@repo/shared/types/sort";
import type { ComponentProps } from "react";

import { cn } from "../lib/utils";

export type IconProps = ComponentProps<"svg">;

// Helper to resolve sprite URL - handles both string imports and Next.js static imports
// Next.js static imports return { src: string, height: number, width: number }
function getSpriteUrl(sprite: string | { src: string }): string {
  return typeof sprite === "string" ? sprite : sprite.src;
}

const sharedIconClassName = "text-muted-foreground shrink-0 size-4";

const Icon = ({
  name,
  className,
  ...props
}: IconProps & {
  name: BaseIconName;
}) => {
  return (
    <svg
      aria-hidden="true"
      className={cn(sharedIconClassName, className)}
      {...props}
    >
      <use href={`${getSpriteUrl(baseSprite)}#${name}`} />
    </svg>
  );
};

const Custom = ({
  name,
  className,
  ...props
}: IconProps & {
  name: CustomIconName;
}) => {
  return (
    <svg
      aria-hidden="true"
      className={cn(sharedIconClassName, className)}
      {...props}
    >
      <use href={`${getSpriteUrl(customSprite)}#${name}`} />
    </svg>
  );
};

const Logo = ({
  name,
  className,
  ...props
}: IconProps & {
  name: LogoIconName;
}) => {
  return (
    <svg
      aria-hidden="true"
      className={cn(sharedIconClassName, className)}
      {...props}
    >
      <use href={`${getSpriteUrl(logoSprite)}#${name}`} />
    </svg>
  );
};

const Connection = ({
  name,
  className,
  ...props
}: IconProps & {
  name: ConnectionIconName;
}) => {
  return (
    <svg
      aria-hidden="true"
      className={cn(sharedIconClassName, className)}
      {...props}
    >
      <use href={`${getSpriteUrl(connectionSprite)}#${name}`} />
    </svg>
  );
};

// export const getDataTypeIcon = (type: DataType): BaseIconName => {
//   switch (type) {
//     case "ARRAY":
//       return "brackets-dots";
//     case "BOOLEAN":
//       return "true-false-boolean";
//     case "CURRENCY":
//       return "currency-dollar";
//     case "PLAIN":
//       return "one-two-three";
//     case "PERCENT":
//       return "percent";
//     case "DATE":
//       return "calendar-day";
//     case "EMAIL":
//       return "at";
//     case "ID":
//       return "tag";
//     case "MONTH":
//       return "calendar-month";
//     case "QUARTER":
//       return "calendar-month";
//     case "NUMBER":
//       return "hashtag";
//     case "OBJECT":
//       return "curly-brackets-dots";
//     case "TEXT":
//       return "text";
//     case "TIME":
//       return "clock";
//     case "TIMESTAMP":
//       return "stopwatch";
//     case "URL":
//       return "link";
//     case "WEEK":
//       return "calendar-week";
//     case "YEAR":
//       return "calendar";
//     case "GEOGRAPHY":
//       return "curly-brackets-dots";
//     case "TIMESINCE":
//       return "dashed-circle-arrow-clockwise-timestamp";
//     case "ENUM":
//       return "curly-brackets-enum";
//   }
// };

const getDateFormatIcon = (
  format: Extract<ColumnFormat, { kind: "DATE" }>,
): BaseIconName => {
  switch (format.dateFormat) {
    case "RELATIVE":
      return "dashed-circle-arrow-clockwise-timestamp";
    case "TIME":
      return "clock";
    case "DATETIME":
      return "stopwatch";
    case "WEEK":
      return "calendar-week";
    case "MONTH":
    case "QUARTER":
      return "calendar-month";
    case "YEAR":
      return "calendar";
    default:
      return "calendar-day";
  }
};

export const getFormatTypeIcon = (format: ColumnFormat): BaseIconName => {
  switch (format.kind) {
    case "ARRAY":
      return "brackets-dots";
    case "BOOLEAN":
      return "true-false-boolean";
    case "CURRENCY":
      return "currency-dollar";
    case "NUMBER_PLAIN":
      return "one-two-three";
    case "PERCENT":
      return "percent";
    case "EMAIL":
      return "at";
    case "NUMBER":
      return "hashtag";
    case "OBJECT":
      return "curly-brackets-dots";
    case "TEXT":
      return "text";
    case "URL":
      return "link";
    case "ENUM":
      return "curly-brackets-enum";
    case "IMAGE":
      return "image";
    case "DATE":
      return getDateFormatIcon(format);
  }
};

const FormatTypeIcon = ({
  format,
  ...rest
}: Omit<IconProps, "name" | "format"> & { format: ColumnFormat }) => {
  return <Icon name={getFormatTypeIcon(format)} {...rest} />;
};

export const getPrimitiveTypeIcon = (type: DataType): BaseIconName => {
  switch (type) {
    case "ARRAY":
      return "brackets-dots";
    case "BOOLEAN":
      return "true-false-boolean";
    case "NUMBER":
      return "hashtag";
    case "OBJECT":
      return "curly-brackets-dots";
    case "STRING":
      return "text";
    case "DATE":
      return "calendar-day";
  }
};

const PrimitiveTypeIcon = ({
  primitiveType,
  ...rest
}: Omit<IconProps, "name"> & { primitiveType: DataType }) => {
  return <Icon name={getPrimitiveTypeIcon(primitiveType)} {...rest} />;
};

const getDatasourceTypeIcon = (type: DatabaseType): LogoIconName => {
  switch (type) {
    case "BIGQUERY":
      return "bigquery";
    case "CLICKHOUSE":
      return "clickhouse";
    case "DATABRICKS":
      return "databricks";
    case "MYSQL":
      return "mysql";
    case "POSTGRESQL":
      return "postgresql";
    case "REDSHIFT":
      return "redshift";
    case "SNOWFLAKE":
      return "snowflake";
  }
};

const DatasourceTypeIcon = ({
  datasourceType,
  managed,
  ...rest
}: Omit<IconProps, "name"> & {
  datasourceType: DatabaseType;
  /** Mora-managed warehouses render the Mora logo instead of the type logo. */
  managed?: boolean;
}) => {
  return (
    <Logo
      name={managed ? "mora" : getDatasourceTypeIcon(datasourceType)}
      {...rest}
    />
  );
};

const getDisplayTypeIcon = (type: DisplayType): BaseIconName => {
  switch (type) {
    case "TABLE":
      return "data-table";
    case "GRAPH":
      return "data-line-chart";
    case "STATISTICS":
      return "data-single-value";
  }
};

const DisplayTypeIcon = ({
  displayType,
  ...rest
}: Omit<IconProps, "name"> & { displayType: DisplayType }) => {
  return <Icon name={getDisplayTypeIcon(displayType)} {...rest} />;
};

const getSortTypeIcon = (sort: SortType, type?: DataType): BaseIconName => {
  if (!sort) return "arrow-down-up-disabled";
  if (type === "NUMBER" || type === "DATE") {
    return sort === "ASC" ? "arrow-down-one-nine" : "arrow-down-nine-one";
  }
  return sort === "ASC" ? "arrow-down-a-z" : "arrow-down-z-a";
};

const SortTypeIcon = ({
  sortType,
  type,
  ...rest
}: Omit<IconProps, "name"> & { sortType: SortType; type?: DataType }) => {
  return <Icon name={getSortTypeIcon(sortType, type)} {...rest} />;
};

const getGraphTypeIcon = (type: GraphType): BaseIconName => {
  switch (type) {
    case "line":
      return "data-line-chart";
    case "area":
      return "data-area-chart";
    case "bar":
      return "data-bar-chart";
    case "scatter":
      return "data-plot-chart";
    case "pie":
      return "data-pie-chart";
  }
};

const GraphTypeIcon = ({
  graphType,
  ...rest
}: Omit<IconProps, "name"> & { graphType: GraphType }) => {
  return <Icon name={getGraphTypeIcon(graphType)} {...rest} />;
};

const IconNamespace = Object.assign(Icon, {
  Custom: Custom,
  Logo: Logo,
  Connection: Connection,
  FormatType: FormatTypeIcon,
  PrimitiveType: PrimitiveTypeIcon,
  DatasourceType: DatasourceTypeIcon,
  DisplayType: DisplayTypeIcon,
  GraphType: GraphTypeIcon,
  SortType: SortTypeIcon,
});

export {
  type BaseIconName,
  type ConnectionIconName,
  type CustomIconName,
  IconNamespace as Icon,
  type LogoIconName,
};
